import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Trophy,
  CheckCircle,
  Shield,
  Zap,
  User,
  Clock,
  Award,
  Send,
} from "lucide-react";
import { motion } from "framer-motion";
import pic1 from "../../assets/images/WhatsApp Image 2025-05-10 at 02.17.14_7561d788.jpg";

const RightTimeSuccess = () => {
  // Animation references
  const heroRef = useRef(null);
  const leadershipRef = useRef(null);
  const foundationsRef = useRef(null);
  const statsRef = useRef(null);
  const solutionsRef = useRef(null);
  const ctaRef = useRef(null);
  const achievementsRef = useRef(null);

  // Counter animation for stats
  const [stats, setStats] = useState({
    employees: 0,
    years: 0,
    clients: 0,
    accuracy: 0,
  });

  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          entry.target.style.opacity = "1";

          // Start counter animation if it's the stats section
          if (entry.target === statsRef.current) {
            startCounters();
          }

          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all sections
    [
      heroRef,
      leadershipRef,
      foundationsRef,
      statsRef,
      solutionsRef,
      ctaRef,
      achievementsRef,
    ].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Counter animation function
  const startCounters = () => {
    const duration = 2000; // 2 seconds
    const frameDuration = 1000 / 60; // 60fps
    const totalFrames = Math.round(duration / frameDuration);

    let frame = 0;

    const finalValues = {
      employees: 600,
      years: 5,
      clients: 350,
      accuracy: 95,
    };

    const counter = setInterval(() => {
      frame++;

      const progress = frame / totalFrames;

      setStats({
        employees: Math.floor(progress * finalValues.employees),
        years: Math.floor(progress * finalValues.years),
        clients: Math.floor(progress * finalValues.clients),
        accuracy: Math.floor(progress * finalValues.accuracy),
      });

      if (frame === totalFrames) {
        clearInterval(counter);
        setStats(finalValues);
      }
    }, frameDuration);
  };

  // Foundation pillars data
  const foundations = [
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Innovation",
      description:
        "We believe in constantly evolving and embracing new technologies and ideas. Innovation drives us to deliver cutting-edge solutions that keep our clients ahead in a competitive marketplace.",
    },
    {
      icon: <User className="h-8 w-8" />,
      title: "Empathy",
      description:
        "Understanding our clients' unique journeys is central to our approach. We listen, adapt, and deliver personalized services that align perfectly with their specific needs and goals.",
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Integrity",
      description:
        "Trust is built on reliability and transparency. We keep our promises and always operate with honesty, ensuring our clients receive dependable services delivered to the highest standard.",
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Agility",
      description:
        "In today's dynamic business environment, flexibility is key. Our agile processes and adaptable teams ensure we can swiftly respond to changing demands—delivering results right on time.",
    },
  ];

  // Solutions data
  const solutions = [
    {
      title: "BPO Services",
      description:
        "Streamline operations with efficient business process outsourcing tailored to your needs.",
    },
    {
      title: "Healthcare Solutions",
      description:
        "Accurate, timely healthcare services including medical billing and transcription support.",
    },
    {
      title: "Digital Marketing",
      description:
        "Data-driven marketing strategies, SEO optimization, content creation, and social media management that accelerate business growth.",
    },
    {
      title: "Inbound Call Solutions",
      description:
        "Deliver exceptional customer service through responsive and efficient inbound call handling.",
    },
    {
      title: "Outbound Call Solutions",
      description:
        "Reach new markets and grow your customer base with targeted, cost-effective outbound calling services.",
    },
    {
      title: "Email and Chat Support",
      description:
        "Enhance customer engagement with tailored email campaigns and real-time chat support strategies.",
    },
    {
      title: "SMS Support",
      description:
        "Engage customers through strategic SMS campaigns offering instant communication and support.",
    },
  ];

  // Form handling
  const [formData, setFormData] = React.useState({
    service: "",
    name: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Here you would normally send the data to your backend
    alert("Form submitted successfully!");
    setFormData({
      service: "",
      name: "",
      email: "",
      phone: "",
      subject: "",
      description: "",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Hero Section */}
      <div
        style={{ marginTop: "80px" }}
        ref={heroRef}
        className=" py-20 opacity-0 bg-blue-900  transition-opacity duration-1000 mb-16"
      >
        <motion.header
          className="bg-blue-900 text-white py-16 text-center shadow-lg"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  <span className="text-white">About </span>
                  <span className="text-blue-600">Us</span>
                </h1>
                <p className="text-white mb-8">
                  At Right On Time Services, we are dedicated to delivering
                  comprehensive business solutions—from BPO and healthcare
                  services to digital marketing and branding strategies. Since
                  our founding in 2020, our passionate team of experts has been
                  committed to helping businesses of all sizes overcome
                  challenges and achieve their objectives with precision and
                  timeliness. In today’s fast-paced digital era, we understand
                  the demands businesses face and stand ready to provide
                  tailored solutions that drive growth, efficiency, and
                  success—right when you need them most.
                </p>
              </div>
            </div>
          </div>
        </motion.header>
      </div>

      {/* Leadership Section */}
      <div
        ref={leadershipRef}
        className="py-20 bg-white opacity-0 transition-opacity duration-1000 mb-16"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-3/3">
              <div className="mb-6">
                <h2 className="text-3xl font-bold mb-2">
                  Leadership <span className="text-blue-600">Perspective</span>
                </h2>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                At Right On Time Services, we believe that every business holds
                the potential to achieve remarkable success. Our mission is to
                be a trusted partner, providing industry-leading outsourced
                services that enable our clients to focus on their core
                strengths. Whether it's medical billing, digital marketing, or
                strategic consulting, our solutions are designed with your
                business goals in mind.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We place innovation, operational excellence, and
                customer-centric solutions at the heart of everything we do. Our
                clients trust us to manage vital yet non-core operations,
                freeing them to concentrate on what truly matters. Our promise?
                To deliver exceptional results—on time, every time.
              </p>
              <p className="text-right text-gray-900 font-semibold italic">
                — Team, Right On Time Services
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Four Foundations Section */}
      <div
        ref={foundationsRef}
        className="py-20 bg-gray-50 opacity-0 transition-opacity duration-1000 mb-16"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Our Four Foundations of Success
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              At Right On Time Services, our approach is guided by four core
              principles that set us apart:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {foundations.map((foundation, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg p-8 transition-all duration-500 hover:shadow-lg hover:-translate-y-2"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-blue-600 mb-4 flex justify-center">
                  <div className="p-3 bg-blue-100 rounded-full">
                    {foundation.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-center">
                  {foundation.title}
                </h3>
                <p className="text-gray-600">{foundation.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div
        ref={statsRef}
        className="py-16 bg-blue-800  opacity-0 transition-opacity duration-1000 mb-16"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Sets Us Apart?</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                {stats.employees}+
              </div>
              <div className="text-sm md:text-base opacity-80">
                Dedicated Professionals
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                {stats.years}+
              </div>
              <div className="text-sm md:text-base opacity-80">
                Years of Excellence
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                {stats.clients}+
              </div>
              <div className="text-sm md:text-base opacity-80">
                Satisfied Clients
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                {stats.accuracy}%
              </div>
              <div className="text-sm md:text-base opacity-80">
                Service Accuracy
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customized Solutions */}
      <div
        ref={solutionsRef}
        className="py-20 bg-white opacity-0 transition-opacity duration-1000 mb-16"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Our Customized <span className="text-blue-600">Solutions</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              At Right On Time Services, we partner with you to unlock business
              growth through tailored solutions. Our expertise spans BPO
              services, advanced healthcare solutions, and strategic digital
              marketing campaigns. Let us handle the complexities of non-core
              operations while you focus on expanding your business horizons.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-md transition-all duration-500 hover:shadow-lg hover:border-blue-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <ArrowRight className="mr-2 h-5 w-5 text-blue-600" />
                  {solution.title}
                </h3>
                <p className="text-gray-600">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div
        ref={ctaRef}
        className="py-8 bg-gradient-to-r from-blue-900 to-indigo-900 opacity-0 transition-opacity duration-1000 mb-6"
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-10">
            {/* Left side - Text */}
            <div className="text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                READY TO START YOUR PROJECT?
              </h2>
              <p className="text-xl mb-8">
                Let's Build Success Together—Right On Time.
              </p>
              <button className="bg-white text-blue-900 px-8 py-3 rounded-md font-semibold hover:bg-blue-100 transition-colors inline-flex items-center">
                Let's Start Together
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>

            {/* Right side - Image */}
            <div className="flex justify-center">
              <img
                src={pic1}
                alt="Project Visual"
                className="max-w-full h-auto rounded-lg "
              />
            </div>
          </div>
        </div>
      </div>

      {/* Achievements Section */}
      <div
        ref={achievementsRef}
        className="py-20 bg-gray-50 opacity-0 transition-opacity duration-1000 mb-16 mb-16"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Achievements & <span className="text-blue-600">Awards</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our success is rooted in collaboration, innovation, and commitment
              to excellence. Partner with us and let's achieve greatness
              together—on time, every time.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-full p-8 shadow-md flex items-center justify-center transition-transform duration-500 hover:scale-110"
              >
                <div className="text-center">
                  <Award className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                  <div className="text-sm font-medium text-gray-600">
                    Achievement {index + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form */}
      {/* <section id="contact" className="py-20 mb-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Get Ready To Start?</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Fill out the form below and we'll get back to you as soon as
              possible.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <label htmlFor="service" className="block text-gray-700 mb-2">
                  Select Service
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a service</option>
                  <option value="bpo">Business Process Outsourcing</option>
                  <option value="healthcare">Healthcare Solutions</option>
                  <option value="digital">Digital Marketing</option>
                  <option value="software">Software Development</option>
                </select>
              </div>
              <div>
                <label htmlFor="name" className="block text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-700 mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-gray-700 mb-2">
                  📞 Your Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="subject" className="block text-gray-700 mb-2">
                  Enter Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="block text-gray-700 mb-2"
                >
                  Project Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows={5}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                ></textarea>
              </div>
              <div className="md:col-span-2 text-center">
                <button
                  style={{ background: "darkblue", color: "white" }}
                  type="submit"
                  className="px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default RightTimeSuccess;
